import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const VastuNavbar = () => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Support both user and admin — admin stores in 'admin_user', user in 'user_data'
  const isAdmin   = !!localStorage.getItem('admin_token');
  const isLoggedIn = !!(localStorage.getItem('user_token') || localStorage.getItem('admin_token'));

  const userData = (() => {
    try {
      const key = isAdmin ? 'admin_user' : 'user_data';
      return JSON.parse(localStorage.getItem(key) || 'null');
    } catch { return null; }
  })();

  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('user_login_time');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_login_time');
    navigate('/login');
  };

  // Initials from name (max 2 letters)
  const initials = userData?.name
    ? userData.name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : (userData?.email ? userData.email[0].toUpperCase() : '?');

  const logoUrl = userData?.logo || null;

  // Avatar component — shows logo image OR initials circle
  const AvatarSmall = () =>
    logoUrl ? (
      <img
        src={logoUrl}
        alt="avatar"
        style={{ ...avatar, objectFit: 'cover', background: '#f0f0f0' }}
        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
      />
    ) : (
      <div style={avatar}>{initials}</div>
    );

  const AvatarLarge = () =>
    logoUrl ? (
      <img
        src={logoUrl}
        alt="avatar"
        style={{ ...dropAvatar, objectFit: 'cover', background: '#f0f0f0' }}
        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
      />
    ) : (
      <div style={dropAvatar}>{initials}</div>
    );

  return (
    <nav style={nav}>
      {/* Brand */}
      <div style={brand} onClick={() => navigate('/projects')} role="button">
        <div style={logoBox}>
          <i className="bi bi-compass-fill" style={{ fontSize: 18, color: '#fff' }} />
        </div>
        <span style={brandText}>VastuAnalyzer</span>
      </div>

      {/* Right */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {!isLoggedIn ? (
          <button onClick={() => navigate('/login')} style={loginBtn}>
            <i className="bi bi-lock-fill" style={{ marginRight: 7 }} />
            Login to access all projects
          </button>
        ) : (
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button onClick={() => setDropdownOpen((o) => !o)} style={profileBtn}>
              <AvatarSmall />
              <div style={{ textAlign: 'left', lineHeight: 1.3 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1F36' }}>
                  {userData?.name || userData?.email || 'User'}
                </div>
                {userData?.email && (
                  <div style={{ fontSize: 11, color: '#5A6478' }}>{userData.email}</div>
                )}
              </div>
              <i
                className={`bi bi-chevron-${dropdownOpen ? 'up' : 'down'}`}
                style={{ fontSize: 11, color: '#A3ABB8', marginLeft: 2 }}
              />
            </button>

            {dropdownOpen && (
              <div style={dropMenu}>
                {/* Profile header */}
                <div style={dropHeader}>
                  <AvatarLarge />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1A1F36', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {userData?.name || userData?.email || 'User'}
                    </div>

                    {isAdmin && (
                      <div style={{ ...badgeStyle, background: '#e0f2fe', color: '#0369a1', marginTop: 4 }}>
                        <i className="bi bi-shield-fill" style={{ fontSize: 10 }} /> Admin
                      </div>
                    )}

                    {userData?.email && (
                      <div style={metaLine}>
                        <i className="bi bi-envelope" style={metaIcon} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {userData.email}
                        </span>
                      </div>
                    )}
                    {userData?.mobile && (
                      <div style={metaLine}>
                        <i className="bi bi-telephone" style={metaIcon} />
                        {userData.mobile}
                      </div>
                    )}
                    {userData?.company_name && (
                      <div style={metaLine}>
                        <i className="bi bi-building" style={metaIcon} />
                        {userData.company_name}
                      </div>
                    )}
                    {userData?.plan_end_date && !isAdmin && (
                      <div style={{ ...metaLine, color: '#f59e0b', marginTop: 5 }}>
                        <i className="bi bi-calendar-check" style={metaIcon} />
                        Plan expires: {new Date(userData.plan_end_date).toLocaleDateString('en-GB')}
                      </div>
                    )}
                  </div>
                </div>

                <div style={divider} />

                <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {/* Profile & Change Password only for regular users */}
                  {!isAdmin && (
                    <>
                      <button
                        onClick={() => { setDropdownOpen(false); navigate('/settings/profile'); }}
                        style={menuItem}
                      >
                        <i className="bi bi-person-gear" style={{ marginRight: 8 }} />
                        Update Profile
                      </button>
                      <button
                        onClick={() => { setDropdownOpen(false); navigate('/settings/change-password'); }}
                        style={menuItem}
                      >
                        <i className="bi bi-lock" style={{ marginRight: 8 }} />
                        Change Password
                      </button>
                      <div style={{ height: 1, background: '#ECEEF2', margin: '2px 0' }} />
                    </>
                  )}
                  <button onClick={handleLogout} style={logoutBtn}>
                    <i className="bi bi-box-arrow-right" style={{ marginRight: 8 }} />
                    Log Out
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default VastuNavbar;

/* ── Styles ─────────────────────────────────────────────────────────────── */
const FF = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";

const nav = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 28px',
  height: 64,
  background: '#FFFFFF',
  borderBottom: '1px solid #ECEEF2',
  boxShadow: '0 1px 0 rgba(20, 30, 60, 0.05)',
  position: 'sticky',
  top: 0,
  zIndex: 1040,
  fontFamily: FF,
};
const brand = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
  textDecoration: 'none',
};
const logoBox = {
  width: 38,
  height: 38,
  borderRadius: 10,
  background: '#00A19D',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: '0 6px 16px rgba(0, 161, 157, 0.25)',
};
const brandText = {
  fontSize: 17,
  fontWeight: 700,
  color: '#1A1F36',
  letterSpacing: -0.3,
};
const loginBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  background: '#FF6B5C',
  color: '#fff',
  border: 'none',
  padding: '10px 18px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 6px 16px rgba(255, 107, 92, 0.30)',
};
const profileBtn = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: '#F5F7FA',
  border: '1px solid #ECEEF2',
  borderRadius: 10,
  padding: '6px 12px 6px 8px',
  cursor: 'pointer',
  fontFamily: FF,
};
const avatar = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: '#DFF5F4',
  color: '#00A19D',
  fontSize: 13,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
const dropMenu = {
  position: 'absolute',
  top: 'calc(100% + 10px)',
  right: 0,
  background: '#FFFFFF',
  border: '1px solid #ECEEF2',
  borderRadius: 14,
  boxShadow: '0 10px 30px rgba(20, 30, 60, 0.12)',
  minWidth: 270,
  zIndex: 1050,
  overflow: 'hidden',
};
const dropHeader = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '16px',
};
const dropAvatar = {
  width: 44,
  height: 44,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #00A19D 0%, #00857E 100%)',
  color: '#fff',
  fontSize: 17,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 600,
};
const metaLine = {
  display: 'flex',
  alignItems: 'center',
  fontSize: 12,
  color: '#5A6478',
  marginTop: 3,
  gap: 4,
};
const metaIcon = {
  fontSize: 11,
  color: '#A3ABB8',
  flexShrink: 0,
};
const divider = {
  height: 1,
  background: '#ECEEF2',
};
const menuItem = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  padding: '9px 12px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  cursor: 'pointer',
  fontFamily: FF,
  textAlign: 'left',
};
const logoutBtn = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  background: '#FFE5E1',
  border: 'none',
  padding: '10px 12px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  color: '#FF6B5C',
  cursor: 'pointer',
  fontFamily: FF,
  textAlign: 'left',
};
